import { useCallback, useEffect, useRef, useState } from 'react'
import { useInRouterContext, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@indipia/shared'
import { Badge, Button, Combobox, Modal, TextLink, type ComboboxOption } from '@indipia/ui-core'
import { orderApi, type OrderVendorRow, type VendorCandidate } from '../../api/orderApi'
import {
  formatCandidateMetrics, isVendorLocked, needsVendorChangeConfirm, ORDER_MESSAGE,
} from '../../constants/materialOrder.logic'
import { VENDOR_CANDIDATES_KEY } from '../../hooks/useMaterialOrders'
import { indexVendorMetrics, vendorMetricsQueryOptions } from '../../hooks/useVendorMetrics'
import { metricFieldsOf } from '../../constants/orderSettings.logic'

// 자재파트너 선택 콤보 — 후보는 서버 정렬 순서 그대로(재정렬 금지), 옵션은 이름·배지·지표 2줄.
// 선택 처리는 호출 화면이 한다(행 없음→A3 assign, 행 있음→A6 replace, '지정 안 함'→A5 release).
// 이 컴포넌트는 후보 조회·옵션 표시·변경 확인 창(상태별)·잠금 사유만 맡는다.

interface Props {
  /** 이 공종의 현재 발주 거래처 행. 행 없음(미배정)이면 null */
  row: OrderVendorRow | null
  taskId: number
  disabled?: boolean
  /** 변경이 막힌 이유(disabled 일 때 콤보 아래 한 줄). 상태가 SHIP_REPLIED·RECEIVED 이면 기본 문구 */
  lockedReason?: string
  /** 후보 선택(변경 확인을 거친 뒤 호출). 호출 화면이 assign/replace 를 실행 */
  onPick: (vendorId: number) => void | Promise<void>
  /** 있으면 맨 앞에 '지정 안 함' 옵션(= DRAFT 이고 발송 기록 없음일 때만 전달) */
  onRelease?: () => void | Promise<void>
  /** 후보 목록에서 뺄 거래처(같은 공종의 다른 거래처 줄에 이미 지정된 곳 — 중복 지정 방지) */
  excludeVendorIds?: number[]
  /** 후보가 없을 때 '자재파트너 등록' 링크를 보일지(기본 true). 좁은 칸(캘린더 표)은 false */
  showRegisterLink?: boolean
}

/** 미선택 표시용 값 — 목록 항목('지정 안 함' = NONE_VALUE)과 겹치지 않아 입력칸이 비고 안내 문구가 보인다 */
const UNSET_VALUE = '__unset__'

const NONE_VALUE = ''

function RegisterLink() {
  const navigate = useNavigate()
  return <TextLink onClick={() => navigate('/vendors/new')}>{ORDER_MESSAGE.vendorRegister}</TextLink>
}

export function VendorPicker({ row, taskId, disabled, lockedReason, onPick, onRelease, excludeVendorIds, showRegisterLink = true }: Props) {
  const qc = useQueryClient()
  const compId = useAuthStore(s => s.user?.compId)
  const inRouter = useInRouterContext()
  const candMapRef = useRef<Map<string, VendorCandidate>>(new Map())
  const [candVersion, setCandVersion] = useState(0)
  const [emptyList, setEmptyList] = useState(false)
  const [pending, setPending] = useState<number | null>(null)   // 변경 확인 창을 기다리는 후보

  const excludeKey = (excludeVendorIds ?? []).join(',')
  const status = row?.orderStatusCode ?? null
  const locked = status != null && isVendorLocked(status)
  const isDisabled = !!disabled || locked
  const lockText = locked ? (lockedReason ?? ORDER_MESSAGE.vendorLockedReason) : lockedReason

  const loadOptions = useCallback(async (): Promise<ComboboxOption[]> => {
    const data = await qc.fetchQuery({
      queryKey: [VENDOR_CANDIDATES_KEY, taskId],
      queryFn: () => orderApi.getVendorCandidates(taskId),
      staleTime: 0,
    })
    // SV2 지표 병합 — 실패하면 지표 줄만 생략(오류 화면 금지). 같은 키를 H-5 옵션과 공유
    const metrics = await qc.fetchQuery(vendorMetricsQueryOptions(compId)).then(indexVendorMetrics).catch(() => null)
    const list = data.candidates
      .filter(c => !excludeVendorIds?.includes(c.vendorId))
      .map(c => (metrics ? { ...c, ...metricFieldsOf(metrics.get(c.vendorId)) } : c))   // 서버 정렬 순서 그대로
    candMapRef.current = new Map(list.map(c => [String(c.vendorId), c]))
    setCandVersion(v => v + 1)
    setEmptyList(list.length === 0)
    return [
      ...(onRelease ? [{ value: NONE_VALUE, label: ORDER_MESSAGE.vendorNone }] : []),
      ...list.map(c => ({ value: String(c.vendorId), label: c.vendorName })),
    ]
  // excludeVendorIds 는 값으로 비교해 배열 신원이 바뀌어도 목록을 다시 만들지 않는다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, taskId, onRelease, compId, excludeKey])

  const renderOption = useCallback((opt: ComboboxOption) => {
    if (opt.value === NONE_VALUE) return <span style={{ color: 'var(--shell-on-surface-2)' }}>{ORDER_MESSAGE.vendorNone}</span>
    const c = candMapRef.current.get(opt.value)
    if (!c) return opt.label
    const current = row != null && c.vendorId === row.vendorId
    return (
      <div className={c.declinedBefore ? 'min-w-0 opacity-50' : 'min-w-0'}>
        <div className="flex items-center gap-1.5 flex-wrap">
          <b className="text-sm" style={{ color: 'var(--shell-on-surface)' }}>{c.vendorName}</b>
          {c.mainVendor && <Badge status="info">{ORDER_MESSAGE.vendorMain}</Badge>}
          {current && <Badge status="success">{ORDER_MESSAGE.vendorCurrent}</Badge>}
          {c.declinedBefore && <Badge status="mute">{ORDER_MESSAGE.vendorDeclinedBefore}</Badge>}
        </div>
        <div className="text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>{formatCandidateMetrics(c)}</div>
      </div>
    )
  // candVersion: 후보 목록이 로드되면 갱신
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.vendorId, candVersion])

  const handleChange = (value: string) => {
    if (value === NONE_VALUE) {
      if (onRelease && row) void onRelease()
      return
    }
    const vendorId = Number(value)
    if (!Number.isFinite(vendorId)) return
    if (row && vendorId === row.vendorId) return   // 현재 거래처 다시 선택 — 변경 없음
    if (row && status != null && needsVendorChangeConfirm(status)) { setPending(vendorId); return }
    void onPick(vendorId)
  }

  // 변경 확인 창이 열려 있는 동안 Esc 는 그 창만 닫는다 — ui-core Modal 은 document 에서 Esc 를 받아
  // 바깥 팝업(C-3)의 Modal 도 같이 닫히므로, 창(window) 캡처 단계에서 먼저 받아 전파를 끊는다.
  useEffect(() => {
    if (pending == null) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      setPending(null)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [pending])

  const confirmPending = () => {
    const id = pending
    setPending(null)
    if (id != null) void onPick(id)
  }

  return (
    <div>
      <Combobox
        value={row ? String(row.vendorId) : UNSET_VALUE}
        displayValue={row ? row.vendorName : ''}
        placeholder={ORDER_MESSAGE.vendorPlaceholder}
        loadOptions={loadOptions}
        renderOption={renderOption}
        onChange={handleChange}
        emptyText={ORDER_MESSAGE.vendorEmpty}
        aria-label={ORDER_MESSAGE.vendorPickerLabel}
        disabled={isDisabled}
        onMouseDown={e => e.stopPropagation()}
      />
      {isDisabled && lockText && <div className="mt-1 text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>{lockText}</div>}
      {emptyList && inRouter && showRegisterLink && <div className="mt-1"><RegisterLink /></div>}

      <Modal
        open={pending != null}
        onClose={() => setPending(null)}
        title={ORDER_MESSAGE.changeVendorTitle}
        size="sm"
        footerAlign="stretch"
        footer={<>
          <Button variant="secondary" onClick={() => setPending(null)}>{ORDER_MESSAGE.cancel}</Button>
          <Button variant="primary" onClick={confirmPending}>{ORDER_MESSAGE.changeVendorOk}</Button>
        </>}
      >
        <div className="text-sm" style={{ color: 'var(--shell-on-surface)' }} onMouseDown={e => e.stopPropagation()}>
          {ORDER_MESSAGE.changeVendorBody}
        </div>
      </Modal>
    </div>
  )
}
